import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import PACSCommunicationInjector from 'app/injectors/PACSCommunication/context';
import {
  isPACSConfigurationDisabled,
  selectPACSConfiguration,
} from 'app/redux/PACSConfiguration/reducer';
import {
  NegociatedAssociationParameters,
  PACSCommunication,
  Result,
} from 'app/interfaces/PACSCommunication';
import { useQuery } from '@tanstack/react-query';
import queryPACSAssociationStatusOptions, {
  addAllPresentationContextsToConfiguration,
} from 'app/queries/queryPACSAssociationStatusOptions';
import { PACSCommunicationContext } from 'app/components/PACSCommunicationProvider/context';

export const DEFAULT_PACS_COMMUNICATION_TIMEOUT = 60;

export type WithPACSCommunication = {
  pacsCommunication: PACSCommunication;
};

export function withPACSCommunication<P extends WithPACSCommunication>(
  Component: React.ComponentType<P>
) {
  return function WrappedComponent(props: Omit<P, keyof WithPACSCommunication>) {
    const pacsCommunication = useContext(PACSCommunicationContext);
    return <Component {...(props as P)} pacsCommunication={pacsCommunication} />;
  };
}

export const IsPACSAssociatedContext =
  React.createContext<NegociatedAssociationParameters>(undefined);

const PACSCommunicationProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const PACSCommunication = useContext(PACSCommunicationInjector);

  if (!PACSCommunication) return <>{children}</>;

  const pacsConfiguration = useSelector(selectPACSConfiguration);
  const isPACSDisabled = isPACSConfigurationDisabled(pacsConfiguration);

  const isEmptyPACSConfiguration = pacsConfiguration === undefined || _.isEmpty(pacsConfiguration);

  const [pacsCommunication, setPacsCommunication] = useState(undefined);

  const associationValidity =
    (pacsConfiguration.timeout ?? DEFAULT_PACS_COMMUNICATION_TIMEOUT) * 1000;
  const { data } = useQuery({
    ...queryPACSAssociationStatusOptions(
      addAllPresentationContextsToConfiguration(pacsConfiguration),
      { pacsCommunication }
    ),
    enabled: !!(pacsCommunication && !isPACSDisabled),
    staleTime: associationValidity,
    gcTime: associationValidity,
    retryDelay: associationValidity / 2,
    refetchInterval: associationValidity / 2,
  });
  // We want store and echo to be enabled
  const isPACSAssociated =
    !isPACSDisabled &&
    _.some(data?.presentation_contexts, ({ result }) => result === Result.Acceptance)
      ? data
      : undefined;

  useEffect(() => {
    if (isPACSDisabled) return;
    setPacsCommunication(
      isEmptyPACSConfiguration ? new PACSCommunication() : new PACSCommunication(pacsConfiguration)
    );

    return () => setPacsCommunication(undefined);
  }, [isPACSDisabled]);

  return (
    <PACSCommunicationContext.Provider value={pacsCommunication}>
      <IsPACSAssociatedContext.Provider value={isPACSAssociated}>
        {children}
      </IsPACSAssociatedContext.Provider>
    </PACSCommunicationContext.Provider>
  );
};

export default PACSCommunicationProvider;
